import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import API from '../api';
import { useAuth } from '../context/AuthContext';
import AdminUsers from './AdminUsers';

const FIELDS_TOP = [
  { key: 'site_name', label: 'Site Name',     type: 'text' },
  { key: 'phone',     label: 'Phone Number',  type: 'text' },
  { key: 'email',     label: 'Contact Email', type: 'email' },
];

const FIELDS_SOCIAL = [
  { key: 'facebook_url',  label: 'Facebook Page URL',  type: 'text', hint: 'e.g. https://facebook.com/sankyomt' },
  { key: 'instagram_url', label: 'Instagram Page URL', type: 'text', hint: 'e.g. https://instagram.com/sankyomt' },
];

const FIELDS_BOTTOM = [
  { key: 'address',       label: 'Office Address',   type: 'textarea' },
  { key: 'about_title',   label: 'About Section Title', type: 'text' },
  { key: 'about_title_ja',   label: 'About Section Title (Japanese)', type: 'text',
    hint: 'Falls back to the English title if left blank.' },
  { key: 'about_content', label: 'About Section Content', type: 'textarea', rows: 8,
    hint: 'Separate paragraphs with a blank line.' },
  { key: 'about_content_ja', label: 'About Section Content (Japanese)', type: 'textarea', rows: 8,
    hint: 'Separate paragraphs with a blank line. Falls back to the English content if left blank.' },
  { key: 'mission_title',   label: 'Mission Section Title', type: 'text' },
  { key: 'mission_title_ja',   label: 'Mission Section Title (Japanese)', type: 'text',
    hint: 'Falls back to the English title if left blank.' },
  { key: 'mission_content', label: 'Mission Section Content', type: 'textarea', rows: 5 },
  { key: 'mission_content_ja', label: 'Mission Section Content (Japanese)', type: 'textarea', rows: 5,
    hint: 'Falls back to the English content if left blank.' },
  { key: 'company_established',     label: 'Company Details: Established', type: 'text', hint: 'e.g. 2016 (Philippines)' },
  { key: 'company_established_ja',  label: 'Company Details: Established (Japanese)', type: 'text',
    hint: 'Falls back to the English value if left blank.' },
  { key: 'company_parent',          label: 'Company Details: Parent Company', type: 'text', hint: 'e.g. Sankyo Seiki Co., Ltd.' },
  { key: 'company_parent_ja',       label: 'Company Details: Parent Company (Japanese)', type: 'text',
    hint: 'Falls back to the English value if left blank.' },
  { key: 'company_headquarters',    label: 'Company Details: Headquarters', type: 'text', hint: 'e.g. Kyoto, Japan' },
  { key: 'company_headquarters_ja', label: 'Company Details: Headquarters (Japanese)', type: 'text',
    hint: 'Falls back to the English value if left blank.' },
  { key: 'company_specialization',    label: 'Company Details: Specialization', type: 'text', hint: 'e.g. Precision Machinery & Measuring Equipment' },
  { key: 'company_specialization_ja', label: 'Company Details: Specialization (Japanese)', type: 'text',
    hint: 'Falls back to the English value if left blank.' },
  { key: 'company_region',          label: 'Company Details: Region', type: 'text', hint: 'e.g. Philippines & South-East Asia' },
  { key: 'company_region_ja',       label: 'Company Details: Region (Japanese)', type: 'text',
    hint: 'Falls back to the English value if left blank.' },
  { key: 'stat_years',       label: 'Stat: Years of Excellence', type: 'text', hint: 'e.g. 70+' },
  { key: 'stat_established', label: 'Stat: Established Year',    type: 'text', hint: 'e.g. 2016' },
  { key: 'stat_products',    label: 'Stat: Products Available',  type: 'text', hint: 'e.g. 100+' },
  { key: 'stat_clients',     label: 'Stat: Major Clients',       type: 'text', hint: 'e.g. 5+' },
];

// phones_extra / emails_extra are stored as a JSON-encoded array (site_settings
// values are plain TEXT), so parse defensively — a fresh install, a hand-edited
// row, or a plain empty string should all just mean "no extra entries yet".
function parseList(value) {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// A labeled list of text inputs with a "+" to add another entry and an "x"
// to remove one — used for the additional phone numbers and emails below.
function RepeatableField({ label, hint, placeholder, values, onChange, type = 'text' }) {
  const update = (i, val) => onChange(values.map((v, idx) => (idx === i ? val : v)));
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  const add = () => onChange([...values, '']);

  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{label}</label>
      <div className="space-y-2">
        {values.map((v, i) => (
          <div key={i} className="flex gap-2">
            <input
              type={type}
              value={v}
              onChange={e => update(i, e.target.value)}
              placeholder={placeholder}
              className="flex-1 border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary"
            />
            <button type="button" onClick={() => remove(i)}
              className="p-2 text-gray-400 hover:text-red-500 border border-gray-200 hover:border-red-300 transition-colors shrink-0">
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add}
        className="mt-2 flex items-center gap-1.5 text-xs font-medium text-primary hover:text-accent transition-colors">
        <PlusIcon className="w-3.5 h-3.5" /> Add another
      </button>
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

const PW_FIELDS = [
  { key: 'currentPassword', label: 'Current Password' },
  { key: 'newPassword',     label: 'New Password' },
  { key: 'confirmPassword', label: 'Confirm New Password' },
];

function GeneralSettingsForm() {
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    API.get('/settings').then(r => setSettings({
      ...r.data,
      phones_extra: parseList(r.data.phones_extra),
      emails_extra: parseList(r.data.emails_extra),
    })).catch(() => {});
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...settings,
        phones_extra: settings.phones_extra.filter(p => p.trim()),
        emails_extra: settings.emails_extra.filter(em => em.trim()),
      };
      await API.put('/settings', payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { alert('Failed to save settings.'); }
    setSaving(false);
  };

  const renderField = ({ key, label, type, rows, hint }) => (
    <div key={key}>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{label}</label>
      {type === 'textarea' ? (
        <textarea
          value={settings[key] || ''}
          onChange={e => setSettings(s => ({ ...s, [key]: e.target.value }))}
          rows={rows || 4}
          className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary resize-y"
        />
      ) : (
        <input
          type={type}
          value={settings[key] || ''}
          onChange={e => setSettings(s => ({ ...s, [key]: e.target.value }))}
          className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary"
        />
      )}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );

  return (
    <form onSubmit={handleSave} className="bg-white border border-gray-100 shadow-sm p-6 space-y-5">
      {FIELDS_TOP.map(renderField)}

      <RepeatableField
        label="Additional Phone Numbers"
        hint="Shown alongside the primary phone number in the footer and contact page."
        placeholder="e.g. +63 917-000-0000"
        values={settings.phones_extra || []}
        onChange={list => setSettings(s => ({ ...s, phones_extra: list }))}
      />

      <RepeatableField
        label="Additional Email Addresses"
        hint="Shown alongside the primary email in the footer and contact page."
        placeholder="e.g. sales@sankyomt.ph"
        type="email"
        values={settings.emails_extra || []}
        onChange={list => setSettings(s => ({ ...s, emails_extra: list }))}
      />

      {FIELDS_SOCIAL.map(renderField)}

      {FIELDS_BOTTOM.map(renderField)}

      <div className="flex items-center gap-4 pt-2">
        <button type="submit" disabled={saving} className="btn-primary text-sm disabled:opacity-60">
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        {saved && (
          <span className="flex items-center gap-1.5 text-sm text-green-600">
            <CheckCircleIcon className="w-4 h-4" /> Settings saved!
          </span>
        )}
      </div>
    </form>
  );
}

function ChangePasswordForm() {
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwStatus, setPwStatus] = useState(null);
  const [pwSaving, setPwSaving] = useState(false);

  const handlePwSave = async (e) => {
    e.preventDefault();
    if (pw.newPassword !== pw.confirmPassword) { setPwStatus('mismatch'); return; }
    if (pw.newPassword.length < 6) { setPwStatus('short'); return; }
    setPwSaving(true);
    try {
      await API.post('/auth/change-password', { currentPassword: pw.currentPassword, newPassword: pw.newPassword });
      setPwStatus('success');
      setPw({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch { setPwStatus('wrong'); }
    setPwSaving(false);
  };

  return (
    <form onSubmit={handlePwSave} className="bg-white border border-gray-100 shadow-sm p-6 space-y-4 max-w-lg">
      {PW_FIELDS.map(({ key, label }) => (
        <div key={key}>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{label}</label>
          <input
            type="password"
            value={pw[key]}
            onChange={e => setPw(p => ({ ...p, [key]: e.target.value }))}
            required
            className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary"
          />
        </div>
      ))}
      {pwStatus === 'mismatch' && <p className="text-red-500 text-sm">New passwords do not match.</p>}
      {pwStatus === 'short' && <p className="text-red-500 text-sm">Password must be at least 6 characters.</p>}
      {pwStatus === 'wrong' && <p className="text-red-500 text-sm">Current password is incorrect.</p>}
      {pwStatus === 'success' && (
        <p className="flex items-center gap-1.5 text-green-600 text-sm"><CheckCircleIcon className="w-4 h-4" /> Password changed successfully!</p>
      )}
      <button type="submit" disabled={pwSaving} className="btn-primary text-sm disabled:opacity-60">
        {pwSaving ? 'Changing...' : 'Change Password'}
      </button>
    </form>
  );
}

export default function AdminSettings() {
  const { admin } = useAuth();
  const isAdmin = admin?.role === 'admin';

  const TABS = [
    isAdmin && { key: 'general', label: 'General' },
    { key: 'password', label: 'Change Password' },
    isAdmin && { key: 'users', label: 'Admin Users' },
  ].filter(Boolean);

  const [tab, setTab] = useState(TABS[0].key);

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-primary-dark">Site Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Edit contact info, content, and account access</p>
      </div>

      {TABS.length > 1 && (
        <div className="flex gap-1 border-b border-gray-200">
          {TABS.map(({ key, label }) => (
            <button key={key} onClick={() => setTab(key)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${tab === key
                ? 'border-accent text-primary'
                : 'border-transparent text-gray-500 hover:text-primary'}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {tab === 'general' && isAdmin && <GeneralSettingsForm />}
      {tab === 'password' && <ChangePasswordForm />}
      {tab === 'users' && isAdmin && <AdminUsers />}
    </div>
  );
}
